<!--
 * @Description: 通用文件上传组件（选文件 + 校验 + 直传 OSS，支持多文件 + v-model）
 * @位置: src/components/global/FileUpload.vue（经 src/components/index.ts 自动全局注册）
 *
 * @用法说明:
 *   选文件后自动校验类型/大小并直传 OSS，上传成功后通过 v-model:value 同步逗号分隔的 fileKey。
 *   支持多文件（maxCount）。父组件用 v-model:value 接收 key 字符串，也可监听 success 事件。
 *   直接 <FileUpload /> 使用，无需手动 import。
 *
 * @Props:
 *   type         上传类型预设 'all'|'image'|'file'|'video'|'audio'，默认 'all'
 *                  all    -> .jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.mp4,.wav,.mp3
 *                  image  -> .jpg,.jpeg,.png,.gif,.webp,.bmp
 *                  file   -> .xls,.xlsx,.doc,.docx,.pdf,.txt,.csv
 *                  video  -> .mp4,.avi,.mov,.wmv,.flv,.mkv,.webm
 *                  audio  -> .mp3,.wav,.aac,.flac,.ogg,.m4a
 *   accept       允许的文件类型（逗号分隔后缀或 MIME 通配如 image/*），传则覆盖 type 预设
 *   maxSize      单个文件大小上限（MB），默认 10，传 0 表示不校验
 *   maxCount     最大文件数量，默认 1
 *   disabled     是否禁用，默认 false（上传中自动禁用）
 *   fileLifeEnum OSS 文件生命周期，默认 'FOREVER'（永久）
 *   duration     预签名 URL 有效期（秒），默认 60
 *   hint         底部提示文案，不传则按 accept 自动生成
 *
 * @v-model:
 *   value        逗号分隔的 fileKey 字符串（只含上传成功的文件）
 *
 * @Events:
 *   success(key: string, file: File)  单个文件上传成功
 *   remove(key: string)                移除文件
 *   error(msg: string)                 上传失败
 *
 * @Expose:
 *   uploading   上传中状态（ref<boolean>）
 *   fileList    文件列表（ref）
 *
 * @示例:
 *   <FileUpload v-model:value="filePath" />
 *   <FileUpload v-model:value="paths" :max-count="8" :max-size="50" />
 *   <FileUpload type="image" :max-size="5" />
 *   <FileUpload accept=".zip,.rar" />
-->
<template>
  <Upload.Dragger
    :file-list="fileList"
    :before-upload="beforeUpload"
    @remove="handleFileRemove"
    :accept="realAccept"
    :disabled="disabled || uploading"
    :multiple="maxCount > 1"
  >
    <p class="ant-upload-drag-icon">
      <InboxOutlined />
    </p>
    <p class="ant-upload-text">{{ $lang('将文件拖到此处，或点击上传') }}</p>
    <p class="ant-upload-hint">{{ hint || ($lang('目前支持的文件类型为') + ' ' + realAccept) }}</p>
  </Upload.Dragger>
</template>

<script setup lang="ts" name="FileUpload">
import { ref, computed, watch } from 'vue'
import { Upload, message } from 'ant-design-vue'
import { InboxOutlined } from '@ant-design/icons-vue'
import type { UploadProps } from 'ant-design-vue'
import axios from 'axios'
import { commonService } from '@/service/api'
import { isSuccess, lang } from '@/utils'

/** 类型预设 -> accept 映射 */
const TYPE_ACCEPT_MAP: Record<string, string> = {
  all: '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.mp4,.wav,.mp3',
  image: '.jpg,.jpeg,.png,.gif,.webp,.bmp',
  file: '.xls,.xlsx,.doc,.docx,.pdf,.txt,.csv',
  video: '.mp4,.avi,.mov,.wmv,.flv,.mkv,.webm',
  audio: '.mp3,.wav,.aac,.flac,.ogg,.m4a'
}

interface Props {
  /** 上传类型预设，与 accept 二选一（accept 优先） */
  type?: 'all' | 'image' | 'file' | 'video' | 'audio'
  /** 允许的文件类型，逗号分隔后缀或 MIME 通配（如 image/*）；传则覆盖 type 预设 */
  accept?: string
  /** 单个文件大小上限（MB），默认 10，传 0 表示不校验 */
  maxSize?: number
  /** 最大文件数量，默认 1 */
  maxCount?: number
  /** 是否禁用 */
  disabled?: boolean
  /** OSS 文件生命周期，默认永久（FOREVER） */
  fileLifeEnum?: string
  /** 预签名 URL 有效期（秒） */
  duration?: number
  /** 底部提示文案，不传则按 accept 自动生成 */
  hint?: string
  /** v-model：逗号分隔的 fileKey 字符串 */
  value?: string
}
const props = withDefaults(defineProps<Props>(), {
  type: 'all',
  accept: '',
  maxSize: 10,
  maxCount: 1,
  disabled: false,
  fileLifeEnum: 'FOREVER',
  duration: 60,
  hint: '',
  value: ''
})

const emit = defineEmits<{
  (e: 'update:value', value: string): void
  (e: 'success', key: string, file: File): void
  (e: 'remove', key: string): void
  (e: 'error', msg: string): void
}>()

/** 实际生效的 accept：accept 优先，否则取 type 预设 */
const realAccept = computed(() => props.accept || TYPE_ACCEPT_MAP[props.type] || TYPE_ACCEPT_MAP.all)

const fileList = ref<any[]>([])
const uploading = ref(false)

/** 校验文件类型是否被接受（支持后缀、MIME 通配、纯 MIME） */
const isAcceptable = (file: File) => {
  const rules = realAccept.value.split(',').map((s) => s.trim()).filter(Boolean)
  return rules.some((rule) => {
    if (rule.endsWith('/*')) {
      return file.type.startsWith(rule.slice(0, -1))
    }
    if (rule.startsWith('.')) {
      return file.name.toLowerCase().endsWith(rule.toLowerCase())
    }
    return file.type === rule
  })
}

/** 获取预签名地址并直传 OSS，返回 fileKey */
const uploadToOss = async (file: File): Promise<string> => {
  const res = await commonService.getUploadUrl({
    size: file.size,
    fileName: file.name,
    fileLifeEnum: props.fileLifeEnum,
    duration: props.duration,
    contentType: file.type
  })
  if (!isSuccess(res) || !res.data) throw new Error(res.msg || lang('获取上传地址失败'))

  const axiosInstance = axios.create({
    timeout: 3600000,
    headers: { 'Content-Type': res.data['content-type'] || file.type }
  })
  const uploadRes = await axiosInstance.put(res.data.url, file)
  if (![200, 201].includes(uploadRes.status)) throw new Error(lang('上传失败'))

  return res.data.key
}

/** 收集所有上传成功的 key，同步到 v-model */
const emitValue = () => {
  const keys = fileList.value.filter((f) => f.status === 'done' && f.key).map((f) => f.key)
  emit('update:value', keys.join(','))
}

const beforeUpload: UploadProps['beforeUpload'] = async (file) => {
  if (!isAcceptable(file)) {
    message.error(lang('文件格式不正确，仅支持') + ' ' + realAccept.value)
    return Upload.LIST_IGNORE
  }
  if (props.maxSize && file.size / 1024 / 1024 > props.maxSize) {
    message.error(lang('文件大小不能超过') + ` ${props.maxSize}MB`)
    return Upload.LIST_IGNORE
  }
  if (fileList.value.length >= props.maxCount) {
    message.error(lang('最多上传') + ` ${props.maxCount} ` + lang('个文件'))
    return Upload.LIST_IGNORE
  }

  const item = { uid: file.uid, name: file.name, status: 'uploading' as string, key: '', originFileObj: file }
  fileList.value.push(item)
  uploading.value = true
  try {
    const key = await uploadToOss(file)
    item.status = 'done'
    item.key = key
    emit('success', key, file)
    emitValue()
  } catch (error: any) {
    item.status = 'error'
    message.error(error?.message || lang('上传失败'))
    emit('error', error?.message || lang('上传失败'))
  } finally {
    uploading.value = false
  }
  return false
}

const handleFileRemove = (file: any) => {
  const target = fileList.value.find((f) => f.uid === file.uid)
  fileList.value = fileList.value.filter((f) => f.uid !== file.uid)
  emit('remove', target?.key || '')
  emitValue()
}

/** 外部清空 value 时同步清空文件列表（支持父组件重置） */
watch(
  () => props.value,
  (val) => {
    if (!val && fileList.value.length) {
      fileList.value = []
    }
  }
)

defineExpose({ uploading, fileList })
</script>
